import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens, Text, Modal, Button } from '@hotjar/hotsauce';
import { bus, siteUrlUpdated } from '@hotjar/webapp/utils/events';
import { updateSite, useCurrentSite } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { SiteUrlForm } from '@hotjar/webapp/features/Activation/SiteUrlForm';
import { MARKS } from '@hotjar/webapp/performance';
import { mark } from '@hotjar/performance';
import { Step } from '../components/Step';

const MOBILE_BREAKPOINT = '(min-width: 960px)';

const Caption = styled(Text)`
  display: flex;
  align-items: center;
  padding: 0 ${tokens.spacer.x2};
  margin: 0 0 ${tokens.spacer.x1};

  @media ${MOBILE_BREAKPOINT} {
    padding: 0 ${tokens.spacer.x4};
    padding: 0;
  }
`;

const Heading = styled(Text)`
  white-space: initial;
  padding: 0 ${tokens.spacer.x2};
  margin: 0 0 ${tokens.spacer.x3};
  overflow: inherit;

  @media ${MOBILE_BREAKPOINT} {
    margin: 0 0 ${tokens.spacer.x4};
    padding: 0 ${tokens.spacer.x4};
    padding: 0;
  }
`;

const FORM_NAME = 'Setup - Site URL';

interface SiteUrlStepProps {
  onSubmit: (url: string) => void;
  onSkip: () => void;
}

const StepContent = ({ onSubmit, onSkip }: SiteUrlStepProps): JSX.Element => {
  const { t } = useTranslation();
  const { data: currentSite } = useCurrentSite();
  const [isSavingUrl, setIsSavingUrl] = useState(false);
  const { formSubmit: trackFormSubmit, click: trackClick } = useTrack();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const handleOnSkip = (eventName = 'Confirm Skip Installation') => {
    mark(MARKS.SIGN_UP_DO_IT_LATER_SUBMISSION_START);
    trackClick(eventName, 'Setup page', {});
    onSkip();
  };

  const handleUrlChange = async (newUrl: string) => {
    mark(MARKS.SIGN_UP_WHERETO_INSTALL_SUBMISSION_START);

    setIsSavingUrl(true);

    if (currentSite) {
      try {
        await updateSite(currentSite, { name: newUrl, url: newUrl });
        bus.publish(siteUrlUpdated());
        trackFormSubmit(FORM_NAME, true, {}, {});
      } catch (e) {
        trackFormSubmit(FORM_NAME, false, {}, {});
      } finally {
        onSubmit(newUrl);
      }
    }
  };

  return (
    <Step data-testid="url-step">
      <Caption textType="caption">{t('activation:setup-wizard.install-hotjar-caption')}</Caption>
      <Heading textType="header4" as="h1">
        {t('activation:setup-wizard.install-hotjar-heading')}
      </Heading>
      <SiteUrlForm
        onSubmit={handleUrlChange}
        isLoading={isSavingUrl}
        onSkip={() => setConfirmationModalOpen(true)}
      />
      <Modal
        size="s"
        contentLabel={t('activation:setup-wizard.skip-confirmation-modal.label')}
        isOpen={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <Modal.Header title={t('activation:setup-wizard.skip-confirmation-modal.title')} />
        <Modal.Body>{t('activation:setup-wizard.skip-confirmation-modal.content')}</Modal.Body>
        <Modal.Footer>
          <Button
            aria-label={t('activation:setup-wizard.skip-confirmation-modal.install-now')}
            kind="ghost"
            onClick={() => setConfirmationModalOpen(false)}
          >
            {t('activation:setup-wizard.skip-confirmation-modal.install-now')}
          </Button>
          <Button
            aria-label={t(
              'activation:setup-wizard.skip-confirmation-modal.continue-without-installing',
            )}
            kind="danger"
            onClick={() => handleOnSkip()}
          >
            {t('activation:setup-wizard.skip-confirmation-modal.continue-without-installing')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Step>
  );
};

export const SiteUrlStep = ({ ...props }: SiteUrlStepProps): JSX.Element => {
  return <StepContent {...props} />;
};
